import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Plus, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MobileFormPage } from '@/components/MobileFormPage';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { ModalOverlay } from '@/components/ModalOverlay';
import { NaveSettingsTabs } from '@/components/NaveSettingsTabs';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId } from '@/lib/offline/operations';
import { fmtInt } from '@/lib/format';
import { useActualizarNave, useBorrarNave, useCrearNave, useNaves } from '@/lib/queries/naves';
import { naveCreateSchema, naveUpdateSchema } from '@/lib/schemas/nave';
import { useAuthStore } from '@/stores/auth';
import type { Nave, NaveCreateInput } from '@/lib/types';

type NaveDraft = {
  codigo: string;
  nombre: string;
  superficie_m2: string;
  capacidad_animales: string;
  tipo_ventilacion: string;
  observaciones: string;
  fecha_alta: string;
};
const EMPTY_DRAFT: NaveDraft = {
  codigo: '',
  nombre: '',
  superficie_m2: '',
  capacidad_animales: '',
  tipo_ventilacion: '',
  observaciones: '',
  fecha_alta: '',
};
function draftFromNave(nave: Nave): NaveDraft {
  return {
    codigo: nave.codigo,
    nombre: nave.nombre,
    superficie_m2: nave.superficie_m2?.toString() ?? '',
    capacidad_animales: nave.capacidad_animales?.toString() ?? '',
    tipo_ventilacion: nave.tipo_ventilacion ?? '',
    observaciones: nave.observaciones ?? '',
    fecha_alta: nave.fecha_alta ?? '',
  };
}
function toPayload(draft: NaveDraft): NaveCreateInput & { nombre: string } {
  return {
    codigo: draft.codigo.trim(),
    nombre: draft.nombre.trim(),
    superficie_m2: draft.superficie_m2 ? Number(draft.superficie_m2) : null,
    capacidad_animales: draft.capacidad_animales ? Number(draft.capacidad_animales) : null,
    tipo_ventilacion: draft.tipo_ventilacion.trim() || null,
    observaciones: draft.observaciones.trim() || null,
    fecha_alta: draft.fecha_alta || null,
  };
}
export function AjustesNavesPage({ creating = false }: { creating?: boolean }) {
  const { naveId: rawId } = useParams();
  const id = useEntityId(rawId, 'naves');
  const owner = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.rol);
  const canManage = role === 'admin' || role === 'ganadero';
  const canRead = canManage || role === 'veterinario';
  const q = useNaves();
  const nave = q.data?.find((n) => n.id === id);
  const selection = useRef<{ key: string; nave?: Nave }>({ key: '' });
  const selectionKey = `${owner}:${rawId}`;
  if (selection.current.key !== selectionKey) selection.current = { key: selectionKey };
  if (nave) selection.current.nave = nave;
  const selected = nave ?? selection.current.nave;
  // Keep an in-flight editor mounted on role revocation; its guard suppresses late completion.
  if ((creating || rawId != null) && (creating || selected))
    return (
      <NaveEditor
        key={`${owner}:${creating ? 'nueva' : rawId}`}
        nave={selected}
        unavailable={!creating && !nave && !q.isLoading}
      />
    );
  return (
    <main
      className={rawId ? 'cm-form-page mx-auto max-w-md px-4' : 'ui-page'}
      data-testid="gestion-naves"
    >
      {rawId && (
        <header className="ui-global-header" data-testid="app-header">
          <AppHeaderContent backTo="/ajustes/naves" backLabel="Naves" />
        </header>
      )}
      <div className="mb-3 flex items-center justify-between gap-3">
        <h1 className="ui-page-title">Naves</h1>
        {canManage && !rawId && (
          <Link to="/ajustes/naves/nueva" className="ui-add-button" aria-label="Nueva nave">
            <Plus size={22} aria-hidden />
          </Link>
        )}
      </div>
      {!canRead ? (
        <p role="alert">No tienes permiso para consultar las naves.</p>
      ) : (
        <>
          {q.isLoading && <p role="status">Cargando naves…</p>}
          {q.isError && (
            <div role="alert" className="parte-error">
              <p>No se pudieron cargar las naves. {extractApiMessage(q.error)}</p>
              <button
                type="button"
                className="ui-secondary mt-3"
                disabled={q.isFetching}
                onClick={() => void q.refetch()}
              >
                Reintentar
              </button>
            </div>
          )}
          {rawId && !q.isLoading && !q.isError && !nave && <p role="alert">Nave no encontrada.</p>}
          {!rawId && q.isSuccess && !q.data?.length && <p>No hay naves todavía.</p>}
          {!rawId && (
            <ul>
              {q.data?.map((item) => (
                <li key={item.id} data-testid={`nave-row-${item.id}`}>
                  <Link to={`/ajustes/naves/${item.id}`} className="ui-menu-row">
                    <span className="min-w-0 break-words">
                      <strong className="block">
                        {item.nombre || item.codigo}
                        {item.nombre && item.nombre !== item.codigo && (
                          <span className="ml-2 text-sm font-normal text-text-secondary">
                            {' '}
                            {item.codigo}
                          </span>
                        )}
                      </strong>
                      <small className="block text-text-secondary">
                        {item.superficie_m2 != null ? `${item.superficie_m2} m² · ` : ''}
                        {item.capacidad_animales != null
                          ? `${fmtInt(item.capacidad_animales)} aves`
                          : 'Capacidad sin definir'}
                      </small>
                    </span>
                    <ChevronRight size={18} className="shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
function NaveEditor({ nave, unavailable = false }: { nave?: Nave; unavailable?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const owner = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.rol);
  const canManage = role === 'admin' || role === 'ganadero';
  const canRead = canManage || role === 'veterinario';
  const crear = useCrearNave(),
    actualizar = useActualizarNave(),
    borrar = useBorrarNave();
  const toast = useToast();
  const [draft, setDraft] = useState<NaveDraft>(() =>
    nave ? draftFromNave(nave) : { ...EMPTY_DRAFT },
  );
  const baseline = useRef(JSON.stringify(draft));
  const initialOwner = useRef(owner),
    farm = useRef(currentBundle()?.explotacion_id),
    mounted = useRef(false),
    sending = useRef(false);
  const [confirmDelete, setConfirmDelete] = useState(false),
    [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  const dirty = canManage && JSON.stringify(draft) !== baseline.current;
  const busy = crear.isPending || actualizar.isPending || borrar.isPending;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (deleted && useAuthStore.getState().user?.id === initialOwner.current)
      navigate('/ajustes/naves', { replace: true });
  }, [deleted, navigate]);
  const current = () =>
    mounted.current &&
    useAuthStore.getState().user?.id === initialOwner.current &&
    useAuthStore.getState().user?.rol === role &&
    (farm.current == null || currentBundle()?.explotacion_id === farm.current);
  async function save(): Promise<string | undefined> {
    if (!canManage || unavailable || sending.current || !current() || !dirty) return;
    const payload = toPayload(draft);
    if (!payload.codigo || !payload.nombre) throw new Error(t('naves.error_basicos'));
    const checked = (nave ? naveUpdateSchema : naveCreateSchema).safeParse(payload);
    if (!checked.success) {
      const issue = checked.error.issues[0];
      throw new Error(t(issue.message, { defaultValue: 'Revisa los datos de la nave.' }));
    }
    sending.current = true;
    try {
      const saved = nave
        ? await actualizar.mutateAsync({
            id: nave.id,
            body: payload,
          })
        : await crear.mutateAsync(payload);
      if (!current()) return;
      baseline.current = JSON.stringify(draft);
      toast.show({ kind: 'success', message: t('naves.guardada') });
      return nave ? '/ajustes/naves' : `/ajustes/naves/${saved.id}`;
    } catch (err) {
      if (current()) throw err;
    } finally {
      sending.current = false;
    }
  }
  async function remove() {
    if (!nave || !canManage || unavailable || sending.current || !current()) return;
    sending.current = true;
    setDeleteError(null);
    try {
      await borrar.mutateAsync(nave.id);
      if (!current()) return;
      setConfirmDelete(false);
      toast.show({ kind: 'success', message: t('naves.borrada') });
      // Deletion also discards this editor's local draft.
      baseline.current = JSON.stringify(draft);
      setDeleted(true);
    } catch (err) {
      if (current()) setDeleteError(extractApiMessage(err));
    } finally {
      sending.current = false;
    }
  }
  const update = (field: keyof NaveDraft, value: string) => {
    if (canManage && !sending.current && current()) setDraft({ ...draft, [field]: value });
  };
  return (
    <MobileFormPage
      title={canRead ? nave?.nombre || nave?.codigo || 'Nueva nave' : 'Nave'}
      backTo="/ajustes/naves"
      backLabel="Naves"
      dirty={dirty}
      externalBusy={borrar.isPending}
      disabled={!canManage || unavailable || busy || !dirty}
      saveLabel={nave ? 'Guardar cambios' : 'Crear nave'}
      onSave={save}
    >
      {!canRead ? (
        <p role="alert">No tienes permiso para consultar las naves.</p>
      ) : unavailable && !borrar.isPending ? (
        <p role="alert">La nave ya no está disponible. Vuelve al listado para actualizar.</p>
      ) : (
        <>
          {nave && <NaveSettingsTabs naveId={nave.id} active="datos" />}
          {!canManage && (
            <p className="cm-form-note">Tu perfil permite consultar los datos de la nave.</p>
          )}
          <fieldset
            disabled={!canManage || busy}
            className="cm-form-fields"
            data-testid="nave-form"
          >
            <div className="cm-form-field">
              <label htmlFor="nave-code">Código</label>
              <input
                id="nave-code"
                data-testid="nave-codigo"
                className="ui-field"
                autoComplete="off"
                maxLength={50}
                required
                value={draft.codigo}
                onChange={(e) => update('codigo', e.target.value)}
              />
              <p className="cm-form-note">Identificador de la nave, por ejemplo N1.</p>
            </div>
            <div className="cm-form-field">
              <label htmlFor="nave-name">Nombre</label>
              <input
                id="nave-name"
                data-testid="nave-nombre"
                className="ui-field"
                maxLength={100}
                required
                value={draft.nombre}
                onChange={(e) => update('nombre', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="cm-form-field min-w-0">
                <label htmlFor="nave-area">Superficie (m²)</label>
                <input
                  id="nave-area"
                  data-testid="nave-superficie_m2"
                  className="ui-field"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={draft.superficie_m2}
                  onChange={(e) => update('superficie_m2', e.target.value)}
                />
              </div>
              <div className="cm-form-field min-w-0">
                <label htmlFor="nave-capacity">Capacidad (aves)</label>
                <input
                  id="nave-capacity"
                  data-testid="nave-capacidad_animales"
                  className="ui-field"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={draft.capacidad_animales}
                  onChange={(e) => update('capacidad_animales', e.target.value)}
                />
              </div>
            </div>
            <div className="cm-form-field">
              <label htmlFor="nave-ventilation">Ventilación</label>
              <input
                id="nave-ventilation"
                data-testid="nave-tipo_ventilacion"
                className="ui-field"
                maxLength={100}
                value={draft.tipo_ventilacion}
                onChange={(e) => update('tipo_ventilacion', e.target.value)}
              />
            </div>
            <div className="cm-form-field">
              <label htmlFor="nave-date">Fecha de alta</label>
              <input
                id="nave-date"
                data-testid="nave-fecha_alta"
                className="ui-field"
                type="date"
                value={draft.fecha_alta}
                onChange={(e) => update('fecha_alta', e.target.value)}
              />
            </div>
            <div className="cm-form-field">
              <label htmlFor="nave-notes">Observaciones</label>
              <textarea
                id="nave-notes"
                className="ui-field"
                rows={2}
                value={draft.observaciones}
                onChange={(e) => update('observaciones', e.target.value)}
              />
            </div>
          </fieldset>
          {nave && canManage && (
            <div className="border-t border-border pt-3">
              <button
                type="button"
                className="ui-text-button text-danger"
                disabled={busy}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDelete(true);
                }}
              >
                <Trash2 size={18} aria-hidden />
                Eliminar nave
              </button>
            </div>
          )}
        </>
      )}
      {confirmDelete && canManage && (
        <ModalOverlay
          placement="bottom"
          ariaLabel="Eliminar nave"
          onBackdrop={() => {
            if (!borrar.isPending) setConfirmDelete(false);
          }}
        >
          <header className="flex items-center justify-between gap-3">
            <h2 className="ui-page-title">Eliminar nave</h2>
            <button
              type="button"
              className="ui-icon-button"
              aria-label="Cerrar confirmación"
              disabled={borrar.isPending}
              onClick={() => setConfirmDelete(false)}
            >
              <X size={22} aria-hidden />
            </button>
          </header>
          <p className="py-3">
            Se eliminará {nave?.nombre || nave?.codigo}. Sólo puedes eliminar naves sin asignaciones
            ni registros vinculados.{dirty ? ' También se descartará este borrador.' : ''}
          </p>
          {deleteError && (
            <p role="alert" className="parte-error mb-3">
              {deleteError}
            </p>
          )}
          <button
            type="button"
            className="ui-secondary w-full"
            disabled={borrar.isPending}
            onClick={() => setConfirmDelete(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="ui-primary mt-2 w-full"
            disabled={borrar.isPending}
            onClick={() => void remove()}
          >
            Borrar
          </button>
        </ModalOverlay>
      )}
    </MobileFormPage>
  );
}
